import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { 
  LayoutDashboard, 
  Kanban, 
  Bell, 
  Users,
  UserCheck,
  LogOut,
  Globe,
  Zap,
  Ticket,
  ShoppingBag,
  ListTodo,
  Activity
} from 'lucide-react';
import { useStore } from '../lib/store';
import { cn } from '../lib/utils';
import { API_URL, getAdminHeaders } from '../lib/config';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { logout, alerts, leads, clients } = useStore();
  const [jobsBadge, setJobsBadge] = useState(0);

  // Fetch active jobs count for badge
  useEffect(() => {
    const fetchJobCount = async () => {
      try {
        const res = await fetch(`${API_URL}/admin/jobs?status=queued,in_progress&limit=1`, {
          headers: getAdminHeaders(),
        });
        if (res.ok) {
          const data = await res.json() as any;
          setJobsBadge(data.totalItems || 0);
        }
      } catch {}
    };
    fetchJobCount();
    const interval = setInterval(fetchJobCount, 30000);
    return () => clearInterval(interval);
  }, []);
  
  const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/clients', icon: UserCheck, label: 'Clients', badge: clients.length },
    { path: '/leads', icon: Users, label: 'Leads', badge: leads.length },
    { path: '/pipeline', icon: ListTodo, label: 'Pipeline', badge: jobsBadge, badgeColor: 'bg-orange-500' },
    { path: '/alerts', icon: Bell, label: 'Relances', badge: alerts.length, badgeColor: 'bg-red-500' },
    { path: '/orders', icon: ShoppingBag, label: 'Commandes' },
    { path: '/chains', icon: Kanban, label: 'Chaînes' },
    { path: '/queue', icon: Activity, label: 'Job Queue' },
    { path: '/promos', icon: Ticket, label: 'Codes promo' },
  ];
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 w-60 h-full bg-white border-r border-gray-200 z-20 flex flex-col">
        {/* Logo */}
        <div className="h-16 px-5 flex items-center border-b border-gray-100">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-gray-900 text-sm leading-tight">Simple Éclair</h1>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">Admin</p>
            </div>
          </Link>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto sidebar-scrollbar">
          {navItems.map(item => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all',
                  isActive 
                    ? 'bg-primary-50 text-primary-700' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                )}
              >
                <item.icon className={cn(
                  'w-[18px] h-[18px]',
                  isActive ? 'text-primary-600' : 'text-gray-400'
                )} />
                <span className="flex-1">{item.label}</span>
                {item.badge !== undefined && item.badge > 0 && (
                  <span className={cn(
                    'min-w-[20px] h-5 px-1.5 flex items-center justify-center text-xs font-semibold rounded-full',
                    item.badgeColor || 'bg-gray-100 text-gray-600',
                    item.badgeColor && 'text-white'
                  )}>
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        
        {/* Divider */}
        <div className="px-3">
          <div className="h-px bg-gray-100" />
        </div>
        
        {/* External Links */}
        <div className="px-3 py-4 space-y-1">
          <a
            href="https://simpleclair.fr"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
          >
            <Globe className="w-[18px] h-[18px] text-gray-400" />
            <span>Site web</span>
          </a>
        </div>
        
        {/* User / Logout */}
        <div className="p-3 border-t border-gray-100">
          <button
            onClick={logout}
            className="flex items-center gap-3 px-3 py-2.5 w-full text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-colors"
          >
            <LogOut className="w-[18px] h-[18px] text-gray-400" />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>
      
      {/* Main content */}
      <main className="ml-60 min-h-screen">
        {children}
      </main>
    </div>
  );
}
